import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import { identity } from "@/data/portfolio";
import { scrollToSection } from "@/components/Nav";

const Line = ({ children, delay, className }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const bgX = useTransform(sx, [-0.5, 0.5], [-28, 28]);
  const bgY = useTransform(sy, [-0.5, 0.5], [-28, 28]);
  const fgX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const fgY = useTransform(sy, [-0.5, 0.5], [14, -14]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(identity.email);
      toast.success("Email copied to clipboard");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = identity.email;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Email copied to clipboard");
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="scanlines relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-16 md:px-12 lg:px-20"
    >
      <motion.div
        aria-hidden="true"
        className="grid-bg absolute -inset-10 opacity-70"
        style={{ x: bgX, y: bgY }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute right-[8%] top-[18%] hidden h-72 w-72 border border-cyan-400/20 lg:block"
        style={{ x: fgX, y: fgY }}
      >
        <div className="absolute -left-px -top-px h-6 w-6 border-l-2 border-t-2 border-cyan-400" />
        <div className="absolute -bottom-px -right-px h-6 w-6 border-b-2 border-r-2 border-cyan-400" />
        <div className="absolute bottom-4 left-4 font-mono text-[10px] leading-relaxed text-zinc-600">
          lat 28.6139° N<br />long 77.2090° E<br />status: ONLINE
        </div>
      </motion.div>

      <div className="relative z-10 max-w-6xl">
        <Line delay={0.1}>
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.35em] text-cyan-400 md:text-sm">
            {"//"} {identity.name} — {identity.location}
          </p>
        </Line>
        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tighter">
          <Line delay={0.25} className="text-[clamp(3rem,11vw,9rem)] text-white">
            Product Lead
          </Line>
          <Line delay={0.4} className="text-[clamp(3rem,11vw,9rem)]">
            <span className="text-outline">& AI Engineer</span>
            <span className="cursor-blink ml-3 inline-block h-[0.75em] w-[0.09em] bg-cyan-400 align-baseline" />
          </Line>
        </h1>
        <Line delay={0.6}>
          <p className="mt-10 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
            {identity.years} years building agentic AI products, deterministic financial
            systems, quant automation and immersive Unity/XR experiences. Now building{" "}
            <span className="text-cyan-400">Prefiler</span> — deterministic financial
            pre-filing review.
          </p>
        </Line>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-view-projects-btn"
            onClick={() => scrollToSection("#projects")}
            className="group flex items-center gap-3 bg-cyan-400 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
          >
            View Projects
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
          <button
            data-testid="hero-copy-email-btn"
            onClick={copyEmail}
            className="group flex items-center gap-3 border border-white/20 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:border-cyan-400 hover:text-cyan-400"
          >
            <Copy className="h-4 w-4" />
            {identity.email}
          </button>
          <a
            data-testid="hero-mailto-link"
            href={`mailto:${identity.email}`}
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-white"
          >
            <Mail className="h-4 w-4" /> Open Mail
          </a>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-white/10 pt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500"
        >
          <span data-testid="stat-experience"><span className="text-white">8+</span> yrs experience</span>
          <span data-testid="stat-roles"><span className="text-white">05</span> LinkedIn roles</span>
          <span data-testid="stat-certs"><span className="text-white">10</span> certifications</span>
          <span data-testid="stat-products"><span className="text-white">50+</span> Unity products</span>
        </motion.div>
      </div>
    </section>
  );
};
